import { useMemo } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Search } from "lucide-react";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { SectionIndexEntry } from "../../services/contractTemplateService";
import { createDataTablePagination } from "./DataTablePagination";

const sectionSidebarPagination = createDataTablePagination({
  variant: "compact"
});

type SectionRow = {
  readonly id: string;
  readonly sectionName: string;
};

type SectionSidebarProps = {
  readonly sections: SectionIndexEntry[];
  readonly selectedSectionId: string | null;
  readonly onSelectSection: (sectionId: string) => void;
  readonly totalCount: number;
  readonly isLoading?: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number) => void;
  readonly searchTerm: string;
  readonly onSearchChange: (term: string) => void;
};

export const SectionSidebar = ({
  sections,
  selectedSectionId,
  onSelectSection,
  totalCount,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  searchTerm,
  onSearchChange,
}: SectionSidebarProps) => {
  const rows: SectionRow[] = useMemo(
    () => sections.map((s) => ({ id: s.id, sectionName: s.section_name })),
    [sections]
  );

  const columns: TableColumn<SectionRow>[] = useMemo(
    () => [
      {
        name: "Section",
        selector: (row) => row.sectionName,
        sortable: false,
        grow: 1,
        cell: (row) => (
          <button
            type="button"
            onClick={() => onSelectSection(row.id)}
            className={`w-full text-left text-sm font-medium transition-colors ${
              row.id === selectedSectionId
                ? "text-primary-500"
                : "text-gray-700 hover:text-primary-600"
            }`}
          >
            <span className="line-clamp-2">{row.sectionName}</span>
          </button>
        )
      }
    ],
    [selectedSectionId, onSelectSection]
  );

  return (
    <div className="flex w-72 shrink-0 flex-col border-r border-gray-200 bg-white">
      <div className="border-b border-gray-200 p-4">
        <p className="mb-2 text-sm font-semibold text-gray-900">
          {totalCount} section{totalCount !== 1 ? "s" : ""}
        </p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="Search sections..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            aria-label="Search sections"
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <DataTable
          columns={columns}
          data={rows}
          customStyles={dataTableCustomStyles}
          striped={false}
          pagination
          paginationServer
          paginationTotalRows={totalCount}
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10]}
          paginationComponent={sectionSidebarPagination}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          progressPending={isLoading}
          noDataComponent={
            <div className="p-8 text-center text-sm text-gray-500">
              No sections match your search.
            </div>
          }
          highlightOnHover
          onRowClicked={(row) => onSelectSection(row.id)}
        />
      </div>
    </div>
  );
};
